import {
  JSNS,
  type JsonObject,
  type AccountAce,
  type GranteeChooser,
  type Mountpoint,
  type NewMountpointSpec,
  type RightPermission,
  type ShareDetails,
  type ShareEmailAddr,
  type ShareInfo,
  type ShareNotification,
  type ShareNotificationAction,
  type ShareOwnerSelector,
  type TargetSpec,
  type Watcher,
  type WatchingTarget,
} from '@zimbra-webif/types';
import type { ZimbraClient } from '../client';

/** Result of CheckPermission. */
export interface CheckPermissionResult {
  /** all requested rights allowed? */
  allow: boolean;
  /** per-right allow flags */
  rights: Array<{ name: string; allow: boolean }>;
}

/**
 * Sharing & delegation (mostly urn:zimbraMail; GetShareInfo is urn:zimbraAccount).
 *
 * Covers the two distinct Zimbra sharing mechanisms:
 *  - account-level ACEs (named rights on the whole account: sendAs,
 *    sendOnBehalfOf, viewFreeBusy, …) via Grant/Revoke/Get/CheckPermission, and
 *  - folder/item share lifecycle around those grants: share notifications,
 *    share discovery, mountpoints (accept-a-share), shared reminders, watchers.
 *
 * Folder grants themselves (`FolderAction op="grant"/"!grant"`) live in the
 * mail-core SDK; the share UI reads effective perms here via
 * `getEffectiveFolderPerms`.
 */
export class ShareApi {
  constructor(private readonly client: ZimbraClient) {}

  /* ---------------- A. Account ACEs / permissions ---------------- */

  /** GrantPermission (urn:zimbraMail) — grant account-level ACEs; echoes them back. */
  async grantPermission(aces: AccountAce[]): Promise<AccountAce[]> {
    const res = await this.client.call<{ ace?: AccountAce[] }>(JSNS.mail, 'GrantPermission', {
      ace: aces,
    });
    return res.ace ?? [];
  }

  /** RevokePermission (urn:zimbraMail) — remove the identified account-level ACEs. */
  async revokePermission(aces: AccountAce[]): Promise<void> {
    await this.client.call(JSNS.mail, 'RevokePermission', { ace: aces });
  }

  /** GetPermission (urn:zimbraMail) — list account ACEs, optionally filtered by right. */
  async getPermission(rights?: string[]): Promise<AccountAce[]> {
    const params: JsonObject = {};
    if (rights?.length) {
      params.ace = rights.map((right) => ({ right }));
    }
    const res = await this.client.call<{ ace?: AccountAce[] }>(JSNS.mail, 'GetPermission', params);
    return res.ace ?? [];
  }

  /** CheckPermission (urn:zimbraMail) — does the grantee/target hold the given rights? */
  async checkPermission(target: TargetSpec, rights: string[]): Promise<CheckPermissionResult> {
    const res = await this.client.call<{ allow?: boolean; right?: RightPermission[] }>(
      JSNS.mail,
      'CheckPermission',
      {
        target,
        right: rights.map((r) => ({ _content: r })),
      },
    );
    return {
      allow: res.allow ?? false,
      rights: (res.right ?? []).map((rp) => ({
        name: rp._content ?? '',
        allow: rp.allow ?? false,
      })),
    };
  }

  /**
   * GetEffectiveFolderPerms (urn:zimbraMail) — the rights the current user
   * effectively holds on a folder, e.g. "rwidx".
   */
  async getEffectiveFolderPerms(folderId: string): Promise<string> {
    const res = await this.client.call<{ folder?: { perm?: string } }>(
      JSNS.mail,
      'GetEffectiveFolderPerms',
      { folder: { l: folderId } },
    );
    return res.folder?.perm ?? '';
  }

  /* ---------------- B. Share notifications & discovery ---------------- */

  /** SendShareNotification (urn:zimbraMail) — email a share offer to grantee(s). */
  async sendShareNotification(
    folderId: string,
    recipients: ShareEmailAddr[],
    opts?: { action?: ShareNotificationAction; notes?: string },
  ): Promise<void> {
    const params: JsonObject = {
      item: { id: folderId },
      e: recipients,
    };
    if (opts?.action) params.action = opts.action;
    if (opts?.notes !== undefined) params.notes = { _content: opts.notes };
    await this.client.call(JSNS.mail, 'SendShareNotification', params);
  }

  /** GetShareNotifications (urn:zimbraMail) — inbound "X shared with you" offers. */
  async getShareNotifications(): Promise<ShareNotification[]> {
    const res = await this.client.call<{ share?: ShareNotification[] }>(
      JSNS.mail,
      'GetShareNotifications',
      {},
    );
    return res.share ?? [];
  }

  /**
   * GetShareInfo (urn:zimbraAccount) — discover shares granted to me, optionally
   * scoped to a particular owner and/or grantee.
   */
  async getShareInfo(opts?: {
    owner?: ShareOwnerSelector;
    grantee?: GranteeChooser;
    internal?: boolean;
    includeSelf?: boolean;
  }): Promise<ShareInfo[]> {
    const params: JsonObject = {};
    if (opts?.internal !== undefined) params.internal = opts.internal;
    if (opts?.includeSelf !== undefined) params.includeSelf = opts.includeSelf;
    if (opts?.grantee) params.grantee = opts.grantee;
    if (opts?.owner) params.owner = opts.owner;
    const res = await this.client.call<{ share?: ShareInfo[] }>(
      JSNS.account,
      'GetShareInfo',
      params,
    );
    return res.share ?? [];
  }

  /** GetShareDetails (urn:zimbraMail) — raw share/ACL detail for one item. */
  async getShareDetails(itemId: string): Promise<ShareDetails> {
    return this.client.call<ShareDetails>(JSNS.mail, 'GetShareDetails', {
      item: { id: itemId },
    });
  }

  /* ---------------- C. Mountpoints & shared reminders ---------------- */

  /** CreateMountpoint (urn:zimbraMail) — accept a share by mounting it locally. */
  async createMountpoint(spec: NewMountpointSpec): Promise<Mountpoint> {
    const res = await this.client.call<{ link?: Mountpoint | Mountpoint[] }>(
      JSNS.mail,
      'CreateMountpoint',
      { link: spec },
    );
    const link = res.link;
    return (Array.isArray(link) ? link[0] : link) ?? ({} as Mountpoint);
  }

  /**
   * EnableSharedReminder (urn:zimbraMail) — toggle reminder popups for a mounted
   * shared calendar.
   */
  async enableSharedReminder(mountpointId: string, showReminders: boolean): Promise<void> {
    await this.client.call(JSNS.mail, 'EnableSharedReminder', {
      link: { id: mountpointId, reminder: showReminders },
    });
  }

  /* ---------------- D. Watchers (feature-gated; optional) ---------------- */

  /** GetWatchers (urn:zimbraMail) — who is watching my items. */
  async getWatchers(): Promise<Watcher[]> {
    const res = await this.client.call<{ watcher?: Watcher[] }>(JSNS.mail, 'GetWatchers', {});
    return res.watcher ?? [];
  }

  /** GetWatchingItems (urn:zimbraMail) — which items am I watching. */
  async getWatchingItems(): Promise<WatchingTarget[]> {
    const res = await this.client.call<{ target?: WatchingTarget[] }>(
      JSNS.mail,
      'GetWatchingItems',
      {},
    );
    return res.target ?? [];
  }
}

// Note: FileSharedWithMe is an internal accept-file-share record and is
// surfaced on the Briefcase domain (see briefcase.ts), not here.
